import * as Constants from './Constants';
import endImageLoadPerfDataPoint from './endImageLoadPerfDataPoint';
import getUrlResponseData, { ResponseData } from './getUrlResponseData';
import { isCrossDomain, getCurrentHostName, getHostNameFromUrl } from './CrossDomainUtils';
import isElementAttached from './isElementAttached';
import loadFromUrl from './loadFromUrl';
import loadImageAsDataUri from './loadImageAsDataUri';
import logDiagnosticData from './logDiagnosticData';
import logImageLoadError from './logImageLoadError';
import retryLoadImage from './retryLoadImage';
import safeGetAttribute from './safeGetAttribute';
import startImageLoadPerfDataPoint from './startImageLoadPerfDataPoint';
import { isFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import { INLINEIMAGE_ATTRIBUTE_ORIGINALSRC } from 'owa-inline-image-consts';

const ERROR_ZERONATURALWIDTH = 'ZeroNaturalWidth';

/**
 * This loads an image from an attachment service url
 * Two key things this function does are:
 * - figure out if it is cross domain or same domain from the url
 * - log performance data point
 * @param img the image
 * @param serviceUrl the attachment url
 * @param context the context, RP, draft or compose
 * @param onSuccessCallback success callback
 * @param onErrorCallback error callback
 * @param retryOnfailure retry on failure
 */
export default function loadFromAttachmentServiceUrl(
    img: HTMLImageElement,
    serviceUrl: string,
    context: string,
    onSuccessCallback?: (imageElement: HTMLImageElement) => void,
    onErrorCallback?: (imageElement: HTMLImageElement) => void,
    retryOnfailure: boolean = false
) {
    const loadDataPoint = startImageLoadPerfDataPoint();
    const crossDomain = isCrossDomain(serviceUrl);
    const howLoad = crossDomain ? Constants.HOWLOAD_CROSSDOMAIN : Constants.HOWLOAD_SAMEDOMAIN;
    const originalSrc = safeGetAttribute(img, INLINEIMAGE_ATTRIBUTE_ORIGINALSRC);

    // loadFromAttachmentServiceUrl could be called from compose where the image tag hasn't gone through Server side tagging processing (in HtmlBodyCallback)
    // i.e. when this is called for a newly updated image in compose.
    // When imageType is not available, just assume it to be AttachmentByCid type
    let imageType = safeGetAttribute(img, Constants.ATTRIBUTE_IMAGETYPE);
    if (imageType.length == 0) {
        imageType = Constants.IMAGETYPE_ATTACHMENTBYCID;
    }

    // Use loaderCode field for logging cors setting
    // For crossdomain, use use-credentials. Otherwise, leave it empty
    let loaderCode = '';
    if (crossDomain) {
        img.setAttribute(
            Constants.ATTRIBUTE_CROSS_ORIGIN,
            Constants.ATTRIBUTE_VALUE_USE_CREDENTIALS
        );
        loaderCode = Constants.ATTRIBUTE_VALUE_USE_CREDENTIALS;
    }

    trace.info(
        `InlineImage: Load attachment, Url: ${serviceUrl}, crossDomain: ${crossDomain}, crossOrigin: ${loaderCode}`
    );

    const field1 = crossDomain ? getCurrentHostName() : '';
    const field2 = crossDomain ? getHostNameFromUrl(serviceUrl) : '';

    loadFromUrl(
        img,
        serviceUrl,
        (imageElement: HTMLImageElement) => {
            if (onSuccessCallback) {
                onSuccessCallback(imageElement);
            }

            const hasNaturalWidth = imageElement.naturalWidth > 0;
            // End the perf data point with an error if naturalWidth is 0
            endImageLoadPerfDataPoint(
                loadDataPoint,
                imageType,
                howLoad,
                hasNaturalWidth ? '' : ERROR_ZERONATURALWIDTH,
                context,
                loaderCode
            );
            if (!hasNaturalWidth) {
                logImageLoadError(
                    imageType /* type */,
                    howLoad /* howLoad */,
                    ERROR_ZERONATURALWIDTH /* error */,
                    '' /* statusCode*/,
                    context /* context */,
                    loaderCode /* loaderCode*/,
                    originalSrc /* originalSrc*/,
                    '' /* contentType */,
                    field1,
                    field2
                );
            }
        },
        (imageElement: HTMLImageElement) => {
            // Bug 28925: Exclude 404 image download error which is a result from deleting conversation (react)
            // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/28925
            // There is race condition that deleting conversation can happen before browser finishing loading image
            // In that case 404 will occur. We want to log "DOMDetached" instead in this case so that we can easily
            // exclude it from report when we want to.
            // This logic is limited to reading pane only. For compose upload, image is initially not attached. They're attached
            // after image is loaded so we should not do this test
            let error = Constants.ERROR_ONERROR;
            if (context == Constants.CONTEXT_READINGPANE && !isElementAttached(imageElement)) {
                if (onErrorCallback) {
                    onErrorCallback(imageElement);
                }
                error = Constants.ERROR_DOM_DETACHED;
                endImageLoadPerfDataPoint(
                    loadDataPoint,
                    imageType,
                    howLoad,
                    error,
                    context,
                    loaderCode
                );
                logImageLoadError(
                    imageType /* type */,
                    howLoad /* howLoad */,
                    error /* error */,
                    '' /* statusCode*/,
                    context /* context */,
                    loaderCode /* loaderCode*/,
                    originalSrc /* originalSrc*/,
                    '' /* contentType */,
                    field1,
                    field2
                );
            } else {
                // In addition to the perf data point, try to get response code and log an error data point
                getUrlResponseData(serviceUrl, crossDomain /* withCredentials */).then(
                    (responseData: ResponseData) => {
                        // Bug 31603: Re-try loading inline image when it is 200 status code (react)
                        // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/31603
                        // Re-try when it is 200, and mime is matched
                        if (
                            retryOnfailure &&
                            responseData.responseCode == 200 &&
                            responseData.mimeMatched
                        ) {
                            retryLoadImage(
                                imageElement,
                                serviceUrl,
                                {
                                    imageType: imageType,
                                    howLoad: howLoad,
                                    context: context,
                                    loaderCode: loaderCode,
                                    originalSrc: originalSrc,
                                    owaDomain: field1,
                                    downloadDomain: field2,
                                    responseData: responseData,
                                    perfDataPoint: loadDataPoint,
                                },
                                onSuccessCallback,
                                onErrorCallback
                            );
                        } else if (
                            responseData.status == Constants.ERROR_IMAGE_MISMATCH &&
                            responseData.expectedMime
                        ) {
                            // Bug 34305: ImageMismatch is one of top errors contributing to inline image failure (after white listing AttNotFound)
                            // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/34305
                            loadImageAsDataUri(
                                imageElement,
                                serviceUrl,
                                {
                                    imageType: imageType,
                                    howLoad: howLoad,
                                    context: context,
                                    loaderCode: loaderCode,
                                    originalSrc: originalSrc,
                                    owaDomain: field1,
                                    downloadDomain: field2,
                                    responseData: responseData,
                                    perfDataPoint: loadDataPoint,
                                },
                                onSuccessCallback,
                                onErrorCallback
                            );
                        } else {
                            // Close off by error callback, and end/log relevant data point
                            if (onErrorCallback) {
                                onErrorCallback(imageElement);
                            }

                            const {
                                status,
                                responseCode,
                                contentType,
                                detectedFormat,
                                first10BytesBase64,
                                mimeMatched,
                            } = responseData;
                            error = status || responseCode.toString(); // Use the new error code from response data
                            endImageLoadPerfDataPoint(
                                loadDataPoint,
                                imageType,
                                howLoad,
                                error,
                                context,
                                loaderCode
                            );

                            logImageLoadError(
                                imageType /* type */,
                                howLoad /* howLoad */,
                                error /* error */,
                                responseCode.toString() /* statusCode*/,
                                context /* context */,
                                loaderCode /* loaderCode*/,
                                originalSrc /* originalSrc*/,
                                contentType /* contentType */,
                                field1,
                                field2,
                                detectedFormat,
                                first10BytesBase64,
                                mimeMatched.toString()
                            );

                            // Bug 27136: Enable error reporting in SDF when there is error for loading inline image (for errors like AttNotFound, NotImage, 404 etc.)
                            // This change is temporary. We would like to get some real repro for the error to troubleshoot the issue
                            if (isFeatureEnabled('mc-imageDiagnostics')) {
                                if (
                                    responseCode == 404 ||
                                    responseCode == 0 ||
                                    responseCode == 200
                                ) {
                                    logDiagnosticData(
                                        img,
                                        error,
                                        responseCode == 200 ? responseData : null
                                    );
                                }
                            }
                        }
                    }
                );
            }
        }
    );
}
